import React, { useCallback } from 'react';

import { RBParsedRail } from 'store/railsBuilder/reducer.types';

import getRecordingsData from 'services/data-manager/get-recordings-data';

import { CollectionItemsList } from 'screens/Collection/types';
import {
    OnSetStripeLoadingStatus,
    RailLoadingStatus,
} from 'screens/Modular/types';

type Params = {
    rail: RBParsedRail | undefined;
    isCollection: boolean;
    setHasNext: React.Dispatch<React.SetStateAction<boolean>>;
    setData: React.Dispatch<React.SetStateAction<CollectionItemsList>>;
    setTotalItems: React.Dispatch<React.SetStateAction<number>>;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    onSetStripeLoadingStatus?: OnSetStripeLoadingStatus;
};

const RECORDINGS_LIMIT = 50;

function useLoadRecordings({
    rail,
    isCollection,
    setHasNext,
    setData,
    setTotalItems,
    setPage,
    onSetStripeLoadingStatus,
}: Params) {
    const onLoadRecordingsNextPage = useCallback(
        async (nextPage: number) => {
            if (rail && rail.properties.RailType) {
                const recordingsRailData = await getRecordingsData(
                    rail,
                    rail.properties.RailType,
                    RECORDINGS_LIMIT,
                    nextPage,
                );

                setPage(nextPage);
                // @ts-ignore
                setData((prevData) => [
                    ...prevData,
                    ...recordingsRailData.recordingSet,
                ]);
                setTotalItems(recordingsRailData.totalRecords);
            }
        },
        [rail, setData, setPage, setTotalItems],
    );

    const onLoadRecordings = useCallback(
        async (silently: boolean) => {
            if (rail && rail.properties.RailType) {
                // get data by `/recordings` API
                try {
                    if (!silently) {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.LOADING,
                        );
                    }

                    const recordingsRailData = await getRecordingsData(
                        rail,
                        rail.properties.RailType,
                        isCollection ? RECORDINGS_LIMIT : rail.properties.limit,
                        1,
                    );

                    setHasNext(
                        recordingsRailData.totalRecords >
                            Number(rail.properties.limit),
                    );
                    setData(recordingsRailData.recordingSet);
                    setTotalItems(recordingsRailData.totalRecords);
                    setPage(1);

                    if (!recordingsRailData.recordingSet.length) {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.EMPTY,
                        );
                    } else {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.LOADED,
                        );
                    }
                } catch (error) {
                    onSetStripeLoadingStatus?.(
                        rail.id,
                        RailLoadingStatus.FAILURE,
                    );
                    setData([]);
                    setTotalItems(0);
                    setPage(1);
                }
            }
        },
        [
            rail,
            isCollection,
            setHasNext,
            setData,
            setTotalItems,
            setPage,
            onSetStripeLoadingStatus,
        ],
    );

    return {
        onLoadRecordings,
        onLoadRecordingsNextPage,
    };
}

export default useLoadRecordings;
